import { Row, UploadProps, notification } from 'antd';
import Dragger from 'antd/lib/upload/Dragger';
import FilePdfOutlined from '@ant-design/icons/FilePdfOutlined';
import {
    DivDownloadTemplate,
    DivInformationTemplateFirstRow,
    InboxOutlinedIcon,
    BrowseSpan,
    SectionTitle,
    CloseIcon,
    PreviewIcon,
} from 'views/custom-declaration/products/bulk-upload/ListBulkUploadFiles.styles';
import FileExcelOutlined from '@ant-design/icons/lib/icons/FileExcelOutlined';
import { TextExtraSmall } from 'components/ui/base/typography';
import Notification from 'components/ui/base/notification/Notification';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Typography } from 'antd';
import Button from 'components/new-ui/base/button/Button';
import { StyledDragger } from 'components/new-ui/base/dragger/styles';
import { InboxOutlined } from '@ant-design/icons';

const { Text: AntText } = Typography;

export type BulkUploadUploadFunc = (
    options: Parameters<NonNullable<UploadProps<any>['customRequest']>>[0]
) => any | Promise<any>;
export type BulkUploadDownloadFunc = () => Promise<{ preSignedUrl: string } | null>;

export enum FileType {
    XLS = 'XLS',
    XLSX = 'XLSX',
    CSV = 'CSV',
}

type Props = {
    id?: string;
    disabled?: boolean;
    onUpload?: BulkUploadUploadFunc;
    onDownload?: BulkUploadDownloadFunc;
    onDelete?: () => void;
    hasDontOverrideFirstRowText?: boolean;
    withNotification?: boolean;
    fileType?: FileType[] | FileType;
    showTitle?: boolean;
    showButton?: boolean;
    showDownloadLink?: boolean;
};

const BulkUpload = ({
    id = 'bulk-file-upload',
    disabled = false,
    onDownload,
    onUpload,
    onDelete,
    hasDontOverrideFirstRowText = false,
    withNotification = true,
    fileType,
    showTitle = true,
    showButton = true,
    showDownloadLink = true,
}: Props) => {
    const { t } = useTranslation('customs_declarations');

    const [file, setFile] = useState<File>();

    const handleUpload = async (options: Parameters<BulkUploadUploadFunc>[0]) => {
        const fileExt = (options.file as File).name.split('.').at(-1)?.toLowerCase();

        if (fileType) {
            if (
                // There is no file extension OR
                !fileExt ||
                // The file extension doesn't match the allowed file types OR
                (typeof fileType === 'string' && fileType.toLowerCase() !== fileExt) ||
                // The file extension doesn't match the allowed file types (Array)
                (Array.isArray(fileType) && !fileType.map((type) => type.toLowerCase()).includes(fileExt))
            ) {
                Notification({
                    type: 'error',
                    messageTitle: t('bulk-upload.file-extension-error-message.title'),
                    description: t('bulk-upload.file-extension-error-message.description'),
                });
                return;
            }
        }

        const response = await onUpload?.(options);

        if (withNotification) {
            if (response) {
                Notification({
                    type: 'success',
                    messageTitle: t('bulk-upload.file-handle-success-message.title'),
                    description: t('bulk-upload.file-handle-success-message.description'),
                });

                setFile(options.file as File);
            } else {
                Notification({
                    type: 'error',
                    messageTitle: t('bulk-upload.file-handle-error-message.title'),
                    description: t('bulk-upload.file-handle-error-message.description'),
                });
            }
        }
    };

    const handleDownload = async () => {
        const response = await onDownload?.();

        if (response) {
            window.open(response.preSignedUrl);
        }
    };

    const handleDelete = async () => {
        setFile(undefined);
        notification.success({ message: 'File deleted successfully.' });
        onDelete?.();
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2.5rem' }}>
                {showTitle && (
                    <SectionTitle className={`${disabled ? 'disabled' : ''}`}>
                        {t('bulk-upload.title-section')}
                    </SectionTitle>
                )}

                {!file && showButton && (
                    <Button
                        disabled={disabled}
                        onClick={() => {
                            document.getElementById(id)?.click();
                        }}
                    >
                        {t('bulk-upload.upload-spreadsheet-file')} <FileExcelOutlined />
                    </Button>
                )}
            </div>
            <div>
                {file ? (
                    <>
                        <Row align="middle">
                            <CloseIcon onClick={handleDelete} />
                            <AntText>
                                <FilePdfOutlined /> {file?.name}
                            </AntText>
                        </Row>
                        <Row>
                            <PreviewIcon /> {t('bulk-upload.success-message')}
                        </Row>
                    </>
                ) : (
                    <div>
                        {!disabled && (
                            <StyledDragger id={id} maxCount={1} customRequest={handleUpload} showUploadList={false}>
                                <div
                                    style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <InboxOutlined style={{ fontSize: 24, color: 'var(--brand-6)' }} />
                                    Click or drag file to this area to upload
                                </div>
                            </StyledDragger>
                        )}
                    </div>
                )}
                {!disabled && showDownloadLink && (
                    <DivDownloadTemplate>
                        <AntText
                            style={{ color: 'inherit' }}
                            onClick={() => !disabled && handleDownload()}
                            disabled={disabled}
                        >
                            <FileExcelOutlined /> {t('bulk-upload.download-spreadsheet-template-link')}
                        </AntText>
                    </DivDownloadTemplate>
                )}
                {hasDontOverrideFirstRowText && (
                    <DivInformationTemplateFirstRow>
                        <TextExtraSmall>{t('bulk-upload.information-template-first-row')}</TextExtraSmall>
                    </DivInformationTemplateFirstRow>
                )}
            </div>
        </>
    );
};

export default BulkUpload;
